"use client";

import { PolicyPagesEnum, IPolicyPagesConfig, WalletLayoutEnum } from "@finbackoffice/fe-core";
import {
    usePoliciesPagesConfig,
    useRuntimeConfig,
    useSiteConfig,
    useSportsbookConfig,
} from "@finbackoffice/site-core";
import { createContext, FC, useCallback, useMemo, type JSX } from "react";
import ActiveLink from "components/base/active-link/ActiveLink";
import Translate from "components/base/translate/Translate";
import { LEGAL_AGE, RouteNames } from "utils/constants";
import { Svg } from "components/base/svg/Svg";
import dynamic from "next/dynamic";
import { AssetsDirectory } from "@finbackoffice/enums";
import Flat from "./layouts/flat/Flat";
import Default from "./layouts/default/Default";
import styles from "./footer.module.sass";

const ExchangeRatePanel = dynamic(
    () => import("components/base/exchange-rate-panel/ExchangeRatePanel"),
    {
        // loading: () => <Loading  />,
        ssr: false,
    },
);

type IFooterContext = {
    enabledPoliciesConfig: IPolicyPagesConfig[];
    renderPromotionsLink: JSX.Element;
    renderPoliciesLink: (item: IPolicyPagesConfig) => JSX.Element | null;
    renderSportLinks: JSX.Element;
    renderCasinoLinks: JSX.Element;
    renderCurrenciesSection: JSX.Element | null;
    renderSocialLinksSection: JSX.Element | null;
    renderExchangeRateSection: JSX.Element | null;
};

export const FooterContext = createContext<IFooterContext>(null as any);

const Footer: FC = () => {
    const { isSportsbookFlat } = useSportsbookConfig();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { enabledPoliciesConfig, enabledPolicySportsbookConfig } = usePoliciesPagesConfig();
    const showExchangeRatePanel = useSiteConfig<boolean>("showExchangeRatePanel");

    const renderPoliciesLink = useCallback(
        (item: IPolicyPagesConfig) => {
            let content;
            switch (item.name) {
                case PolicyPagesEnum.Privacy:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.PRIVACY}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_privacy" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Terms:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.TERMS}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_terms" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Sports:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.SPORTSBOOK}/${enabledPolicySportsbookConfig?.[0]?.name.toLowerCase().replace(/_/g, "-")}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_rules" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Casino:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.CASINO}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_casino" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Payments:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.PAYMENTS}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_payments" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.ResponsibleGaming:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.RESPONSIBLE_GAMING}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_responsible_gaming" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Cookies:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.COOKIES}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_cookies" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.SelfExclusion:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.SELF_EXCLUSION}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_self_exclusion" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.DisputeResolution:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.DISPUTE_RESOLUTION}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_dispute_resolution" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.AntiMoneyLaundering:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.ANTI_MONEY_LAUNDERING}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_anti_money_laundering" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.FairnessRngTesting:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.FAIRNESS_RNG_TESTING}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_fairness_rng_testing" />
                        </ActiveLink>
                    );
                    break;
                case PolicyPagesEnum.Kyc:
                    content = (
                        <ActiveLink
                            key={item.name}
                            href={`/${RouteNames.POLICIES}/${RouteNames.KYC}`}
                            activeClassName={styles.active}>
                            <Translate tid="pageName_policies_kyc" />
                        </ActiveLink>
                    );
                    break;
                default:
                    content = null;
            }
            return content;
        },
        [enabledPolicySportsbookConfig],
    );

    const renderPromotionsLink = useMemo(
        () => (
            <ActiveLink
                href={`/${RouteNames.BONUSES}/${RouteNames.PROMOTIONS}`}
                activeClassName={styles.active}>
                <Translate tid="pageName_bonuses_promotions" />
            </ActiveLink>
        ),
        [],
    );

    const renderSportLinks = useMemo(
        () => (
            <>
                <ActiveLink
                    href={`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`}
                    includes={`/${RouteNames.SPORT}/${RouteNames.LIVE}/${RouteNames.ALL}`}
                    activeClassName={styles.active}>
                    <Translate tid="pageName_live" />
                </ActiveLink>
                <ActiveLink
                    href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.ALL}`}
                    includes={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}`}
                    activeClassName={styles.active}>
                    <Translate tid="pageName_sport" />
                </ActiveLink>
                <ActiveLink
                    href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.UPCOMING}`}
                    activeClassName={styles.active}>
                    <Translate tid="pageName_upcoming_matches" />
                </ActiveLink>
                <ActiveLink
                    href={`/${RouteNames.SPORT}/${RouteNames.UPCOMING}`}
                    activeClassName={styles.active}>
                    <Translate tid="pageName_calendar" />
                </ActiveLink>
                {COMMON_SITE_CONFIGS.sportResults && (
                    <ActiveLink
                        href={`/${RouteNames.SPORT}/${RouteNames.RESULTS}`}
                        activeClassName={styles.active}>
                        <Translate tid="pageName_results" />
                    </ActiveLink>
                )}
            </>
        ),
        [COMMON_SITE_CONFIGS.sportResults],
    );

    const renderCasinoLinks = useMemo(
        () => (
            <>
                {COMMON_SITE_CONFIGS.contentVerticals.livecasino && (
                    <ActiveLink
                        href={`/${RouteNames.CASINO}/${RouteNames.LIVE_CASINO}`}
                        includes={`/${RouteNames.LIVE_CASINO}`}
                        activeClassName={styles.active}>
                        <Translate tid="pageName_livecasino" />
                    </ActiveLink>
                )}
                {COMMON_SITE_CONFIGS.contentVerticals.slots && (
                    <ActiveLink
                        href={`/${RouteNames.CASINO}/${RouteNames.SLOTS}`}
                        includes={`/${RouteNames.SLOTS}`}
                        activeClassName={styles.active}>
                        <Translate tid="pageName_slots" />
                    </ActiveLink>
                )}
            </>
        ),
        [
            COMMON_SITE_CONFIGS.contentVerticals.livecasino,
            COMMON_SITE_CONFIGS.contentVerticals.slots,
        ],
    );

    const renderSocialLinks = useMemo(
        () => (
            <>
                {COMMON_SITE_CONFIGS.socialPlatforms.facebook && (
                    <a
                        href={`https://facebook.com/${COMMON_SITE_CONFIGS.socialPlatforms.facebook}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/facebook.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}

                {COMMON_SITE_CONFIGS.socialPlatforms.instagram && (
                    <a
                        href={`https://instagram.com/${COMMON_SITE_CONFIGS.socialPlatforms.instagram}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/instagram.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}
                {COMMON_SITE_CONFIGS.socialPlatforms.twitter && (
                    <a
                        href={`https://twitter.com/${COMMON_SITE_CONFIGS.socialPlatforms.twitter}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/twitter.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}

                {COMMON_SITE_CONFIGS.socialPlatforms.youtube && (
                    <a
                        href={`https://youtube.com/@${COMMON_SITE_CONFIGS.socialPlatforms.youtube}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/youtube.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}
                {COMMON_SITE_CONFIGS.socialPlatforms.telegram && (
                    <a
                        href={`https://t.me/${COMMON_SITE_CONFIGS.socialPlatforms.telegram}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/telegram.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}
                {COMMON_SITE_CONFIGS.socialPlatforms.tiktok && (
                    <a
                        target="_blank"
                        href={`https://tiktok.com/@${COMMON_SITE_CONFIGS.socialPlatforms.tiktok}`}
                        className={styles.socialLink}>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/tiktok.svg`}
                            className="svg-icon"
                        />
                    </a>
                )}
            </>
        ),
        [
            COMMON_SITE_CONFIGS.socialPlatforms.facebook,
            COMMON_SITE_CONFIGS.socialPlatforms.instagram,
            COMMON_SITE_CONFIGS.socialPlatforms.telegram,
            COMMON_SITE_CONFIGS.socialPlatforms.tiktok,
            COMMON_SITE_CONFIGS.socialPlatforms.twitter,
            COMMON_SITE_CONFIGS.socialPlatforms.youtube,
        ],
    );

    const renderCurrenciesList = useMemo(() => {
        if (COMMON_SITE_CONFIGS.wallet.type === WalletLayoutEnum.Pix) {
            return (
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/pix.svg`}
                    className="svg-icon"
                />
            );
        }

        return (
            <>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/bitcoin.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/ripple.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/dogecoin.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/litecoin.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/tron.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/usdt.svg`}
                    className="svg-icon"
                />
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Footer}/ethereum.svg`}
                    className="svg-icon"
                />
            </>
        );
    }, [COMMON_SITE_CONFIGS.wallet.type]);

    const renderCurrenciesSection = useMemo(() => {
        if (COMMON_SITE_CONFIGS.wallet.type === WalletLayoutEnum.Tola) {
            return null;
        }

        return <div className={styles.footerCrypto}>{renderCurrenciesList}</div>;
    }, [COMMON_SITE_CONFIGS.wallet.type, renderCurrenciesList]);

    const renderSocialLinksSection = useMemo(
        () => (
            <nav className={styles.socialLinks}>
                <span className={styles.age}>{LEGAL_AGE}+</span>
                {renderSocialLinks}
            </nav>
        ),
        [renderSocialLinks],
    );

    const renderExchangeRateSection = useMemo(
        () =>
            showExchangeRatePanel ? (
                <div className={styles.fxRate}>
                    <ExchangeRatePanel />
                </div>
            ) : null,
        [showExchangeRatePanel],
    );

    const value = useMemo(
        () => ({
            enabledPoliciesConfig,
            renderPromotionsLink,
            renderPoliciesLink,
            renderSportLinks,
            renderCasinoLinks,
            renderCurrenciesSection,
            renderSocialLinksSection,
            renderExchangeRateSection,
        }),
        [
            enabledPoliciesConfig,
            renderPromotionsLink,
            renderPoliciesLink,
            renderSportLinks,
            renderCasinoLinks,
            renderCurrenciesSection,
            renderSocialLinksSection,
            renderExchangeRateSection,
        ],
    );

    return (
        <FooterContext.Provider value={value}>
            {isSportsbookFlat ? <Flat /> : <Default />}
        </FooterContext.Provider>
    );
};

export default Footer;
